import React, { useState, useEffect, useMemo } from 'react';
import { MDXProvider } from '@mdx-js/react';
import { useGuide } from '../hooks/useHelp';
import { Screenshot } from './Screenshot';
import { Callout } from './Callout';
import styles from './GuideViewer.module.css';

// קומפוננטות MDX זמינות בתוך קובצי המדריך
const MDX_COMPONENTS = { Screenshot, Callout };

interface GuideViewerProps {
  roleId: number;
  onClose?: () => void;
}

export const GuideViewer: React.FC<GuideViewerProps> = ({ roleId }) => {
  const { Content, frontmatter, loading, notFound } = useGuide(roleId);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // useMemo כדי לא ליצור reference חדש בכל render (מונע ריצה מחדש של IntersectionObserver)
  const sections = useMemo(
    () => (frontmatter?.sections as Array<{ id: string; title: string }>) ?? [],
    [frontmatter]
  );
  const title = (frontmatter?.title as string) ?? 'מדריך';

  // סנכרון קטע פעיל לפי מיקום גלילה
  useEffect(() => {
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((e) => e.isIntersecting);
        if (visible) setActiveSection(visible.target.id);
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  if (loading) {
    return (
      <div className={styles.root}>
        <div className={styles.center}>
          <div className={styles.spinner} aria-label="טוען מדריך..." />
          <span>טוען מדריך...</span>
        </div>
      </div>
    );
  }

  if (notFound || !Content) {
    return (
      <div className={styles.root}>
        <div className={styles.center}>
          <span aria-live="polite">מדריך אינו זמין לתפקיד זה</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
      </div>

      <div className={styles.body}>
        {/* תוכן עניינים */}
        {sections.length > 0 && (
          <nav className={styles.toc} aria-label="תוכן עניינים">
            <p className={styles.tocTitle}>תוכן עניינים</p>
            {sections.map(({ id, title: sectionTitle }) => (
              <button
                key={id}
                className={`${styles.tocItem} ${activeSection === id ? styles.tocItemActive : ''}`}
                onClick={() => scrollTo(id)}
                aria-current={activeSection === id ? 'location' : undefined}
              >
                {sectionTitle}
              </button>
            ))}
          </nav>
        )}

        {/* תוכן MDX */}
        <div className={styles.content}>
          <MDXProvider components={MDX_COMPONENTS}>
            <Content />
          </MDXProvider>
        </div>
      </div>
    </div>
  );
};
